import { useMemo } from 'react';
import { datadogRum } from '@datadog/browser-rum';

import {
    CombinedAccount,
    CombinedBankDataResult,
    CombinedInstitution,
    StatementStatus,
    TransactionStatus,
} from './index.interface';
import { getAccountSyncState, getLastAccountSyncDate } from './accountSyncState';

import { useMember } from 'modules/common/queries/member';
import useStatementHistoryConnectorId from 'modules/common/hooks/useStatementHistoryConnectorId';
import useHurdlrPlaidItems from 'services/hurdlr/queries/useHurdlrPlaidItems';
import {
    BankAccountStatus,
    BankAccount,
    PlaidItem,
} from 'services/hurdlr/queries/useHurdlrPlaidItems/useHurdlrPlaidItems.interface';
import useBankStatementsAvailabilityList from 'services/bookkeeping/queries/useBankStatementsAvailabilityList';
import {
    BankStatementAvailabilityItem,
    BankStatementsStatus,
} from 'services/bookkeeping/queries/useBankStatementsAvailabilityList/useBankStatementsAvailabilityList.interface';
import useRelayPortalAccounts from 'services/bank/queries/useRelayPortalAccounts';
import {
    isAutoStatementsWithdrawn,
    useUnreliablePlaidInstitutionIds,
} from 'services/quiltt/utils/unreliableInstitutions';

/**
 * Map Quiltt connection status to our statement status enum.
 * Relay portal accounts (signed up through Collective) are always treated as enabled.
 */
function getStatementStatus(
    quilttAccount: BankStatementAvailabilityItem | undefined,
    isRelayPortalAccount: boolean
): StatementStatus {
    if (isRelayPortalAccount) {
        return 'enabled';
    }

    if (!quilttAccount) {
        return 'not_enabled';
    }

    const { connection_status: connectionStatus } = quilttAccount;

    switch (connectionStatus) {
        case BankStatementsStatus.Connected:
            return 'enabled';
        case BankStatementsStatus.Available:
            return 'available';
        case BankStatementsStatus.Unsupported:
            return 'unsupported';
        case BankStatementsStatus.ErrorRepairable:
            return 'error_repairable';
        default:
            return 'not_enabled';
    }
}

/**
 * Get transaction status from Plaid account and item
 */
function getTransactionStatus(account: BankAccount, plaidItem: PlaidItem): TransactionStatus {
    // Account state is read first, so a dead account is still reported as one when its item is
    // also erroring — the precedence this page has always used.
    const accountState = getAccountSyncState(account);
    if (accountState === 'paused') {
        return 'inactive';
    }

    if (accountState === 'notSyncing') {
        return 'not_syncing';
    }

    // Check if there's a Plaid error at the item level
    const hasError = Boolean(plaidItem.apiErrorMessage) || Boolean(plaidItem.apiErrorCode);
    if (hasError) {
        datadogRum.addAction('Plaid item error detected', {
            type: 'PLAID_ITEM_ERROR',
            plaidItemId: plaidItem.id,
            errorMessage: plaidItem.apiErrorMessage,
            errorCode: plaidItem.apiErrorCode,
        });
        return 'error';
    }

    return 'synced';
}

const RELAY_INSTITUTION_ID = 'ins_117228';
/**
 * Hook that combines Plaid bank accounts (transactions) with Quiltt availability (statements)
 * into a unified data structure for the combined Bank Accounts page.
 */
export default function useCombinedBankData(): CombinedBankDataResult {
    const { data: member } = useMember();
    const statementHistoryConnectorId = useStatementHistoryConnectorId();
    const unreliablePlaidIds = useUnreliablePlaidInstitutionIds();

    // Fetch Plaid bank accounts (transactions)
    const {
        data: plaidItems = [],
        isLoading: isLoadingPlaid,
        isError: isPlaidError,
    } = useHurdlrPlaidItems({
        memberId: member?.id,
        status: BankAccountStatus.Active,
        include_bank_accounts: true,
    });

    // Fetch Quiltt availability (statements)
    const {
        data: quilttAccounts = [],
        isLoading: isLoadingQuiltt,
        isError: isQuilttError,
    } = useBankStatementsAvailabilityList();

    // Fetch Relay portal accounts (accounts created through Collective's portal)
    const { data: relayPortalData, isLoading: isLoadingRelay } = useRelayPortalAccounts(member?.id);
    const relayPortalLast4s = useMemo(() => new Set(relayPortalData?.portal_account_last4s ?? []), [relayPortalData]);

    // Combine the data
    const institutions = useMemo((): CombinedInstitution[] => {
        if (!plaidItems.length) {
            return [];
        }

        // Create a map of Quiltt accounts by apiAccountId for quick lookup
        const quilttAccountMap = new Map<string, BankStatementAvailabilityItem>();
        quilttAccounts.forEach((account) => {
            quilttAccountMap.set(account.apiAccountId, account);
        });

        // Transform each Plaid item into a CombinedInstitution
        return plaidItems.map((plaidItem): CombinedInstitution => {
            const accounts: CombinedAccount[] = (plaidItem.bank_accounts || []).map((account): CombinedAccount => {
                const quilttAccount = quilttAccountMap.get(account.apiAccountId);
                const isRelayPortalAccount =
                    plaidItem.apiInstitutionId === RELAY_INSTITUTION_ID && relayPortalLast4s.has(account.apiAccountNo);
                const statementStatus = getStatementStatus(quilttAccount, isRelayPortalAccount);
                const transactionStatus = getTransactionStatus(account, plaidItem);

                return {
                    // Identifiers
                    id: account.id,
                    apiAccountId: account.apiAccountId,
                    apiAccountNo: account.apiAccountNo,

                    // Account info
                    name: account.apiAccountName,
                    officialName: account.apiOfficialName,
                    type: account.apiAccountType,
                    subType: account.apiSubType,

                    // Transaction status
                    transactionStatus,
                    syncTransactions: account.syncTransactions,
                    lastSyncedDate: getLastAccountSyncDate(account)?.toISOString() ?? null,

                    // Statement status
                    statementStatus,
                    connectionStatus: quilttAccount?.connection_status ?? null,
                    connectionId: quilttAccount?.connection_id ?? null,
                    connectorId: quilttAccount?.connector_id ?? null,

                    // Relay portal flag
                    isRelayPortalAccount,
                };
            });

            // Get institution-level Quiltt info from the matched accounts
            // Since we matched accounts by apiAccountId above, use those matched accounts
            // to get the connector_id and connection_id for the institution
            const firstAccountWithConnector = accounts.find((a) => a.connectorId);
            // Members owed older statements get the longer-history connector (same rule as Accounting
            // Setup, see useStatementHistoryConnectorId); everyone else keeps the connector Quiltt recorded here.
            const connectorId = statementHistoryConnectorId ?? firstAccountWithConnector?.connectorId ?? null;
            const firstAccountWithConnection = accounts.find((a) => a.connectionId);
            const connectionId = firstAccountWithConnection?.connectionId ?? null;

            // Calculate aggregated status
            // Relay portal accounts are excluded — their sync cannot be stopped, so they don't
            // contribute to the institution-level "Stop statement sync" visibility.
            const hasEnabledStatements = accounts.some(
                (a) => a.statementStatus === 'enabled' && !a.isRelayPortalAccount
            );
            const allAccountsUnsupported = accounts.every((a) => a.statementStatus === 'unsupported');
            const hasErrorRepairableConnection = accounts.some((a) => a.statementStatus === 'error_repairable');
            // A missing value counts as true, so an older backend keeps offering Auto Statements.
            const everyAccountOffered = (plaidItem.bank_accounts || []).every(
                (account) => quilttAccountMap.get(account.apiAccountId)?.offer_auto_statements !== false
            );
            const offerAutoStatements = !isAutoStatementsWithdrawn(
                everyAccountOffered,
                plaidItem.apiInstitutionId,
                unreliablePlaidIds
            );
            const allAccountsRelayPortal = accounts.length > 0 && accounts.every((a) => a.isRelayPortalAccount);

            if (plaidItem.apiInstitutionId === RELAY_INSTITUTION_ID && !allAccountsRelayPortal) {
                datadogRum.addAction('Relay institution has mixed portal accounts', {
                    type: 'RELAY_MIXED_PORTAL_ACCOUNTS',
                    plaidItemId: plaidItem.id,
                    totalAccounts: accounts.length,
                    portalAccountCount: accounts.filter((a) => a.isRelayPortalAccount).length,
                });
            }

            const hasPlaidError =
                Boolean(plaidItem.apiErrorMessage) ||
                Boolean(plaidItem.apiErrorCode) ||
                Boolean(plaidItem.apiErrorType);

            // Get error timestamp from the first account with error_repairable status
            const errorRepairableAccount = (plaidItem.bank_accounts || []).find((account) => {
                const quilttAccount = quilttAccountMap.get(account.apiAccountId);
                return quilttAccount?.error_repairable_at;
            });
            const errorRepairableAt = errorRepairableAccount
                ? quilttAccountMap.get(errorRepairableAccount.apiAccountId)?.error_repairable_at ?? null
                : null;

            return {
                // Institution identifiers
                institutionId: plaidItem.apiInstitutionId,
                institutionName: plaidItem.apiInstitutionName,
                plaidItemId: plaidItem.id,

                // Plaid item info
                apiAccessToken: plaidItem.apiAccessToken,
                lastSyncedDate: plaidItem.lastInstitutionTranDate ?? null,
                hasPlaidError,
                plaidErrorMessage: plaidItem.apiErrorMessage,

                // Quiltt connection info
                connectorId,
                connectionId,

                // Aggregated status
                hasEnabledStatements,
                allAccountsUnsupported,
                hasErrorRepairableConnection,
                allAccountsRelayPortal,
                offerAutoStatements,

                // Error timestamp
                errorRepairableAt,

                // Accounts
                accounts,
            };
        });
    }, [plaidItems, quilttAccounts, relayPortalLast4s, statementHistoryConnectorId, unreliablePlaidIds]);

    return {
        institutions,
        isLoading: isLoadingPlaid || isLoadingQuiltt || isLoadingRelay,
        isError: isPlaidError || isQuilttError,
    };
}
